import { useCallback, useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, Bell, Clock3, FileSearch, Landmark, Link2,
  Loader2, MapPin, Presentation, Radar, RefreshCw, ScrollText, ShieldCheck,
} from "lucide-react";
import { api } from "../api";
import PolandMap from "../components/PolandMap";
import { StatusBadge, MAP_DOT_COLORS } from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";

// Kraków, obręb 0014 — zweryfikowana działka z uchwalonym MPZP (rich evidence flow)
const PARCEL_ID = "126104_9.0014.67/20";

const STEPS = [
  { id: "opening", label: "Wprowadzenie" },
  { id: "parcel-example", label: "Działka" },
  { id: "planning-status", label: "Status" },
  { id: "evidence-provenance", label: "Dowody" },
  { id: "due-diligence-report", label: "Raport" },
  { id: "planning-watch", label: "Monitoring" },
  { id: "gmina-readiness", label: "Indeks" },
  { id: "closing", label: "Kontakt" },
];

const BAND_ORDER = ["gotowa", "w_trakcie", "ograniczona", "brak_danych"];
const BAND_LABELS = {
  gotowa: "GOTOWA", w_trakcie: "W TRAKCIE",
  ograniczona: "OGRANICZONA INFORMACJA", brak_danych: "BRAK DANYCH",
};
const NO_DATA_DISCLAIMER =
  "Brak wykrycia w krajowej usłudze WMS nie oznacza braku planu — stan wymaga formalnej weryfikacji w gminie.";
const DOCTRINE = "AI przygotowuje. Człowiek decyduje.";

const fmtTs = (iso) =>
  iso ? new Date(iso).toLocaleString("pl-PL", { timeZone: "UTC", hour12: false }) + " UTC" : "—";

function useGet(path, params) {
  const [state, setState] = useState({ data: null, error: false });
  const load = useCallback(() => {
    setState({ data: null, error: false });
    api.get(path, { params })
      .then((r) => setState({ data: r.data, error: false }))
      .catch(() => setState({ data: null, error: true }));
  }, [path]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(load, [load]);
  return { ...state, reload: load };
}

const SourceState = ({ data, error, onRetry, testId, children }) => {
  if (error) {
    return (
      <div data-testid={testId} className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-6 text-center">
        <p className="text-sm text-slate-600">
          Źródło danych jest chwilowo niedostępne. PlanGmina nie podstawia danych demonstracyjnych — spróbuj ponownie.
        </p>
        <Button data-testid={`${testId}-retry`} variant="outline" size="sm" className="mt-3" onClick={onRetry}>
          <RefreshCw size={14} className="mr-2" /> Odśwież
        </Button>
      </div>
    );
  }
  if (!data) {
    return (
      <div data-testid={`${testId}-loading`} className="flex items-center justify-center gap-2 p-8 text-sm text-slate-500">
        <Loader2 size={18} className="animate-spin" /> Pobieranie danych z oficjalnych źródeł…
      </div>
    );
  }
  return children;
};

const StepShell = ({ index, children }) => (
  <section
    data-testid={`pink-demo-step-${index + 1}-${STEPS[index].id}`}
    className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-10"
  >
    <p className="font-heading text-xs font-bold uppercase tracking-widest text-[#DC143C]">
      Krok {index + 1} z {STEPS.length} · {STEPS[index].label}
    </p>
    {children}
  </section>
);

const NoDataBadge = ({ testId }) => (
  <span data-testid={testId} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-600">
    <span className="h-1.5 w-1.5 rounded-full bg-current" /> BRAK DANYCH
  </span>
);

function StepOpening() {
  const cards = [
    { icon: ShieldCheck, title: "Redukcja ryzyka nabycia gruntu", text: "Status planistyczny działki przed decyzją inwestycyjną — z oficjalnych rejestrów, nie z ogłoszenia." },
    { icon: Clock3, title: "Wstępna analiza w minuty", text: "Jedno wyszukiwanie zamiast ręcznego przeszukiwania BIP-ów, geoportali i rejestrów województw." },
    { icon: FileSearch, title: "Dowody i źródła, nie opinie", text: "Każdy fakt ma organ źródłowy, link i znacznik czasu pobrania. Dane oficjalne są oddzielone od interpretacji." },
  ];
  return (
    <div className="mt-4">
      <h1 className="font-heading text-3xl font-extrabold leading-tight tracking-tight text-slate-900 sm:text-5xl">
        Co naprawdę wiemy o tej działce przed zakupem lub inwestycją?
      </h1>
      <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg">
        PlanGmina porządkuje dane planistyczne na poziomie konkretnej działki na potrzeby
        profesjonalnego due diligence — od Planu Ogólnego Gminy, przez MPZP, po monitoring zmian.
      </p>
      <div className="mt-8 grid gap-4 sm:grid-cols-3">
        {cards.map((c) => (
          <div key={c.title} className="rounded-xl border border-slate-200 bg-slate-50 p-5">
            <c.icon size={20} className="text-[#DC143C]" />
            <p className="mt-3 font-heading text-sm font-bold text-slate-900">{c.title}</p>
            <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{c.text}</p>
          </div>
        ))}
      </div>
      <p data-testid="pink-demo-doctrine" className="mt-8 inline-block rounded-full border border-[#DC143C]/30 bg-[#FEF2F2] px-4 py-1.5 font-heading text-xs font-bold uppercase tracking-widest text-[#DC143C]">
        {DOCTRINE}
      </p>
    </div>
  );
}

function StepParcel({ res }) {
  return (
    <SourceState data={res.data} error={res.error} onRetry={res.reload} testId="pink-demo-parcel-source">
      {res.data && (
        <div className="mt-4 grid gap-6 lg:grid-cols-2">
          <div>
            <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              Zweryfikowana, prawdziwa działka — Kraków
            </h2>
            <p className="mt-2 text-sm text-slate-600">
              Przykład na żywo z krajowych rejestrów. Żadnych danych demonstracyjnych.
            </p>
            <dl className="mt-6 divide-y divide-slate-100 rounded-xl border border-slate-200">
              {[
                ["parcel-number", "Numer działki (TERYT)", res.data.parcel.full_number, true],
                ["parcel-gmina", "Gmina", res.data.parcel.gmina_name],
                ["parcel-obreb", "Obręb", res.data.parcel.obreb],
                ["parcel-powiat", "Powiat", res.data.parcel.powiat],
                ["parcel-wojewodztwo", "Województwo", res.data.parcel.wojewodztwo],
                ["parcel-centroid", "Współrzędne (WGS84)", res.data.parcel.centroid ? `${res.data.parcel.centroid.lat}, ${res.data.parcel.centroid.lon}` : "—", true],
                ["parcel-geometry", "Geometria działki", "Dostępna (EPSG:2180)"],
              ].map(([tid, k, v, mono]) => (
                <div key={tid} className="flex items-center justify-between gap-4 px-4 py-2.5">
                  <dt className="text-xs uppercase tracking-wide text-slate-500">{k}</dt>
                  <dd data-testid={`pink-demo-${tid}`} className={`text-right text-sm font-semibold text-slate-900 ${mono ? "font-mono-data break-all" : ""}`}>{v || "—"}</dd>
                </div>
              ))}
            </dl>
            <div className="mt-4 flex items-center gap-2">
              <span data-testid="pink-demo-official-badge" className="rounded border border-emerald-400 bg-emerald-50 px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-emerald-700">
                Dane oficjalne
              </span>
              <span className="text-xs text-slate-500">Źródło: GUGiK ULDK · pobrano {fmtTs(res.data.retrieved_at)}</span>
            </div>
          </div>
          <div className="overflow-hidden rounded-xl border border-slate-200">
            <PolandMap
              gminas={[{ id: "pink-demo-parcel", name: res.data.parcel.full_number, lat: res.data.parcel.centroid?.lat, lng: res.data.parcel.centroid?.lon, color: "#DC143C" }]}
              onSelect={() => {}}
              labels={false}
              testId="pink-demo-parcel-map"
            />
            <p className="border-t border-slate-100 bg-slate-50 px-4 py-2 text-xs text-slate-500">
              <MapPin size={12} className="mr-1 inline" /> Lokalizacja działki na mapie Polski
            </p>
          </div>
        </div>
      )}
    </SourceState>
  );
}

function StepStatus({ res }) {
  return (
    <SourceState data={res.data} error={res.error} onRetry={res.reload} testId="pink-demo-status-source">
      {res.data && (() => {
        const pog = res.data.planning.plan_ogolny;
        const mpzp = res.data.planning.mpzp;
        return (
          <div className="mt-4">
            <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              Status planistyczny działki
            </h2>
            <p className="mt-2 text-sm text-slate-600">
              Stan na {fmtTs(res.data.retrieved_at)} — z krajowych usług Geoportalu.
            </p>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <div data-testid="pink-demo-pog-card" className="rounded-xl border border-slate-200 p-5">
                <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">Plan Ogólny Gminy</p>
                <div className="mt-3">
                  {pog.status === "adopted" ? <StatusBadge status="adopted" label="Uchwalony" />
                    : pog.status === "draft" ? <StatusBadge status="draft" label="Projekt w rejestrze" />
                    : <NoDataBadge testId="pink-demo-pog-nodata" />}
                </div>
                <p className="mt-3 text-sm text-slate-600">
                  {pog.status === "unknown" ? NO_DATA_DISCLAIMER
                    : "Krajowy rejestr Geoportalu potwierdza obejmowanie tej lokalizacji."}
                </p>
              </div>
              <div data-testid="pink-demo-mpzp-card" className="rounded-xl border border-slate-200 p-5">
                <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">MPZP (plan miejscowy)</p>
                <div className="mt-3">
                  {mpzp.status === "existing" ? <StatusBadge status="existing" label="Uchwalony MPZP" /> : <NoDataBadge testId="pink-demo-mpzp-nodata" />}
                </div>
                {(mpzp.plans || []).length > 0 && (
                  <ul data-testid="pink-demo-mpzp-plans" className="mt-3 space-y-1.5">
                    {mpzp.plans.map((p) => (
                      <li key={p} className="rounded-lg bg-slate-50 px-3 py-2 text-sm font-medium text-slate-800">{p}</li>
                    ))}
                  </ul>
                )}
                {mpzp.status !== "existing" && <p className="mt-3 text-sm text-slate-600">{NO_DATA_DISCLAIMER}</p>}
              </div>
            </div>
          </div>
        );
      })()}
    </SourceState>
  );
}

function StepEvidence({ res }) {
  return (
    <SourceState data={res.data} error={res.error} onRetry={res.reload} testId="pink-demo-evidence-source">
      {res.data && (
        <div className="mt-4">
          <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
            Dowody i pochodzenie danych
          </h2>
          <blockquote data-testid="pink-demo-provenance-quote" className="mt-4 border-l-4 border-[#DC143C] bg-slate-50 px-5 py-4 font-heading text-base font-semibold text-slate-900 sm:text-lg">
            PlanGmina nie pokazuje tylko odpowiedzi. Pokazuje również, skąd ta odpowiedź pochodzi.
          </blockquote>
          <div className="mt-6 overflow-x-auto rounded-xl border border-slate-200">
            <table data-testid="pink-demo-provenance-table" className="w-full min-w-[720px] border-collapse text-left text-xs">
              <thead>
                <tr className="bg-slate-900 text-white">
                  <th className="px-3 py-2.5">Fakt</th>
                  <th className="px-3 py-2.5">Wartość</th>
                  <th className="px-3 py-2.5">Organ źródłowy</th>
                  <th className="px-3 py-2.5">Źródło (URL)</th>
                  <th className="px-3 py-2.5">Pobrano</th>
                  <th className="px-3 py-2.5">Weryfikacja</th>
                </tr>
              </thead>
              <tbody>
                {res.data.evidence.map((e, i) => (
                  <tr key={i} data-testid={`pink-demo-provenance-row-${i}`} className="border-b border-slate-100 align-top last:border-0">
                    <td className="px-3 py-2.5 font-medium text-slate-900">{e.fact_pl}</td>
                    <td className="max-w-[220px] px-3 py-2.5 text-slate-700">{e.value_pl}</td>
                    <td className="px-3 py-2.5 text-slate-700">{e.source_authority}</td>
                    <td className="px-3 py-2.5">
                      {e.source_url ? (
                        <a data-testid={`pink-demo-provenance-link-${i}`} href={e.source_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-medium text-[#DC143C] hover:underline">
                          <Link2 size={12} /> Link
                        </a>
                      ) : "—"}
                    </td>
                    <td className="px-3 py-2.5 font-mono-data text-[11px] text-slate-500">{fmtTs(e.retrieved_at)}</td>
                    <td className="px-3 py-2.5">
                      <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${e.verification_status === "źródło oficjalne" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                        {e.verification_status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </SourceState>
  );
}

function StepReport({ preview }) {
  return (
    <SourceState data={preview.data} error={preview.error} onRetry={preview.reload} testId="pink-demo-report-source">
      {preview.data && (
        <div className="mt-4">
          <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
            Raport due diligence działki
          </h2>
          <p className="mt-2 text-sm text-slate-600">
            Podgląd generowany na żywo, bez zapisu w systemie i bez danych osobowych.
          </p>
          <div data-testid="pink-demo-report-preview-card" className="mt-6 rounded-xl border-2 border-slate-900">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b-2 border-slate-900 px-5 py-4">
              <div>
                <p className="font-heading text-xs font-bold uppercase tracking-widest text-[#DC143C]">PlanGmina · Raport</p>
                <p data-testid="pink-demo-report-id" className="mt-1 font-mono-data text-sm font-semibold text-slate-900">{preview.data.report_id}</p>
              </div>
              <span className="rounded border border-emerald-400 bg-emerald-50 px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-emerald-700">Dane oficjalne</span>
            </div>
            <div className="grid gap-4 px-5 py-4 sm:grid-cols-2">
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">Działka</p>
                <p className="mt-1 break-all font-mono-data text-sm font-semibold text-slate-900">{preview.data.parcel_identification.number}</p>
                <p className="mt-1 text-xs text-slate-600">
                  gmina {preview.data.parcel_identification.gmina} · powiat {preview.data.parcel_identification.powiat} · woj. {preview.data.parcel_identification.wojewodztwo}
                </p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">Dowody w raporcie</p>
                <p className="mt-1 text-sm font-semibold text-slate-900">{preview.data.evidence_table.length} faktów z tabeli dowodowej · {preview.data.sources?.length ?? 0} źródeł z datą pobrania</p>
              </div>
            </div>
            <div className="border-t border-slate-200 px-5 py-4">
              <p className="text-xs uppercase tracking-wide text-slate-500">Suma kontrolna SHA-256 (integralność dowodów)</p>
              <p data-testid="pink-demo-report-hash" className="mt-1 break-all font-mono-data text-xs text-slate-900 sm:text-sm">{preview.data.report_hash}</p>
              <div className="mt-3 grid gap-2 text-xs text-slate-600 sm:grid-cols-2">
                <p>Wygenerowano: <span data-testid="pink-demo-report-generated" className="font-mono-data">{fmtTs(preview.data.generated_at)}</span></p>
                <p>Migawka źródeł: <span data-testid="pink-demo-report-snapshot" className="font-mono-data">{fmtTs(preview.data.source_snapshot_at)}</span></p>
              </div>
              <p className="mt-3 text-xs leading-relaxed text-slate-500">
                Identyczna migawka źródeł daje identyczną sumę kontrolną — odbiorca raportu może zweryfikować,
                że dokument nie został zmieniony. Raport oczekuje na weryfikację przez urbanistę, radcę prawnego lub architekta.
              </p>
            </div>
            <div className="border-t border-slate-200 bg-slate-50 px-5 py-3">
              <p className="font-heading text-[10px] font-bold uppercase tracking-widest text-[#DC143C]">{DOCTRINE}</p>
            </div>
          </div>
        </div>
      )}
    </SourceState>
  );
}

function StepWatch() {
  const flow = [
    { icon: MapPin, title: "Zapisz działkę", text: "Użytkownik zapisuje prawdziwą działkę z pełną migawką źródeł." },
    { icon: Radar, title: "Codzienna weryfikacja", text: "System codziennie ponownie odpytuje oficjalne źródła — bez cache." },
    { icon: Bell, title: "Alert o zmianie", text: "E-mail z porównaniem poprzedniej i nowej wartości oraz źródłem." },
  ];
  return (
    <div className="mt-4">
      <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
        Planning Watch — monitoring zmian planistycznych
      </h2>
      <div className="mt-6 grid gap-4 sm:grid-cols-3">
        {flow.map((f, i) => (
          <div key={f.title} data-testid={`pink-demo-watch-step-${i + 1}`} className="relative rounded-xl border border-slate-200 bg-slate-50 p-5">
            <f.icon size={20} className="text-[#DC143C]" />
            <p className="mt-3 font-heading text-sm font-bold text-slate-900">{i + 1}. {f.title}</p>
            <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{f.text}</p>
          </div>
        ))}
      </div>
      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <div data-testid="pink-demo-watch-current" className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-5">
          <p className="font-heading text-xs font-bold uppercase tracking-widest text-emerald-700">Dostępne dziś</p>
          <ul className="mt-3 space-y-2 text-sm text-slate-700">
            <li>· Status Planu Ogólnego Gminy</li>
            <li>· Status MPZP dla działki</li>
            <li>· Pokrycie MPZP (plany miejscowe)</li>
          </ul>
        </div>
        <div data-testid="pink-demo-watch-planned" className="rounded-xl border border-slate-200 bg-slate-50 p-5">
          <p className="font-heading text-xs font-bold uppercase tracking-widest text-slate-500">W rozwoju</p>
          <ul className="mt-3 space-y-2 text-sm text-slate-500">
            <li>· Strefy planistyczne planu ogólnego</li>
            <li>· Szczegóły uchwał MPZP (przeznaczenie, parametry)</li>
            <li>· Kolejne rejestry krajowe i nakładki środowiskowe</li>
          </ul>
        </div>
      </div>
      <p className="mt-4 text-xs text-slate-500">
        Alerty dotyczą wyłącznie danych oficjalnych — nigdy interpretacji AI.
      </p>
    </div>
  );
}

function StepIndex({ stats }) {
  return (
    <SourceState data={stats.data} error={stats.error} onRetry={stats.reload} testId="pink-demo-index-source">
      {stats.data && (
        <div className="mt-4">
          <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
            Indeks Gotowości Gmin — cała Polska
          </h2>
          <p className="mt-2 text-sm text-slate-600">
            Warstwa krajowa: {stats.data.total.toLocaleString("pl-PL")} gmin, każda ze statusem popartym dowodem
            i znacznikiem czasu. Ostatnia aktualizacja: {fmtTs(stats.data.last_update)}.
          </p>
          <div data-testid="pink-demo-gmina-index-stats" className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {BAND_ORDER.map((b) => (
              <div key={b} data-testid={`pink-demo-band-${b}`} className="rounded-xl border border-slate-200 p-4">
                <div className="flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: MAP_DOT_COLORS[b] }} />
                  <p className="font-heading text-xs font-bold uppercase tracking-wider text-slate-700">{BAND_LABELS[b]}</p>
                </div>
                <p className="mt-2 font-heading text-3xl font-extrabold text-slate-900">
                  {(stats.data.by_band?.[b] ?? 0).toLocaleString("pl-PL")}
                </p>
              </div>
            ))}
          </div>
          <p data-testid="pink-demo-index-coverage" className="mt-3 text-xs text-slate-500">
            Zbadano {(stats.data.harvested ?? 0).toLocaleString("pl-PL")} z {stats.data.total.toLocaleString("pl-PL")} gmin — pozostałe są w kolejce codziennej aktualizacji.
          </p>
          <Link data-testid="pink-demo-index-link" to="/indeks" className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#DC143C] hover:underline">
            Zobacz interaktywny indeks <ArrowRight size={14} />
          </Link>
        </div>
      )}
    </SourceState>
  );
}

function StepClosing({ onCta, onMainSite, onPilot }) {
  return (
    <div className="mt-4">
      <h2 className="font-heading text-2xl font-bold leading-snug tracking-tight text-slate-900 sm:text-3xl">
        PlanGmina może wspierać wstępną analizę gruntów, due diligence oraz monitoring zmian
        planistycznych dla inwestorów, deweloperów i doradców.
      </h2>
      <p className="mt-4 max-w-2xl text-sm leading-relaxed text-slate-600">
        PlanGmina nie zastępuje prawników, urbanistów, rzeczoznawców majątkowych, organów administracji
        ani formalnego due diligence prawno-planistycznego. Porządkuje oficjalne dane i ich pochodzenie,
        aby specjaliści mogli decydować szybciej.
      </p>
      <div data-testid="pink-demo-pilot" className="mt-8 rounded-xl border-2 border-[#DC143C]/25 bg-[#FEF2F2]/60 p-5 sm:p-6">
        <p className="font-heading text-base font-bold text-slate-900 sm:text-lg">
          Chcesz przetestować PlanGmina na własnej działce lub portfelu?
        </p>
        <Button data-testid="pink-demo-cta-pilot" size="lg" className="mt-3 bg-[#DC143C] text-white hover:bg-[#B91C1C]" asChild>
          <a href="mailto:contact@HussarHoldings.pl?subject=PlanGmina%20%E2%80%94%20zainteresowanie%20pilota%C5%BCem" onClick={onPilot}>
            Umów pilotaż <ArrowRight size={16} className="ml-2" />
          </a>
        </Button>
      </div>
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <Button data-testid="pink-demo-cta-collaboration" size="lg" variant="outline" onClick={onCta}>
          Porozmawiajmy o możliwej współpracy <ArrowRight size={16} className="ml-2" />
        </Button>
        <Button data-testid="pink-demo-cta-main-site" size="lg" variant="outline" asChild>
          <Link to="/" onClick={onMainSite}>Przejdź do PlanGmina.pl</Link>
        </Button>
      </div>
      <p data-testid="pink-demo-corporate" className="mt-8 text-sm text-slate-500">
        PlanGmina — projekt rozwijany przez Hussar Holdings LLC
        <span className="mx-2 text-slate-300">·</span>
        <a href="mailto:contact@HussarHoldings.pl" className="font-medium text-slate-600 underline-offset-2 hover:text-[#DC143C] hover:underline">contact@HussarHoldings.pl</a>
        <span className="mx-2 text-slate-300">·</span>
        <a href="https://hussarholdings.pl" target="_blank" rel="noopener noreferrer" className="font-medium text-slate-600 underline-offset-2 hover:text-[#DC143C] hover:underline">HussarHoldings.pl</a>
      </p>
      <p className="mt-8 font-heading text-[10px] font-bold uppercase tracking-widest text-[#DC143C]">{DOCTRINE}</p>
    </div>
  );
}

export default function PinkDemoPage() {
  const [step, setStep] = useState(0);
  const [vertical, setVertical] = useState(false);
  const [ctaOpen, setCtaOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const refParam = searchParams.get("ref");
  const ref = refParam && /^[a-z0-9_-]{1,32}$/.test(refParam) ? refParam : null;
  // Aggregate-only campaign telemetry: event + ref bucket, no identifiers
  const demoEvent = useCallback((event) => {
    // F5 (2026-10-04): entering the demo route IS the product event — campaign
    // attribution (ref) is metadata, never a precondition. Direct /demo/pink
    // visits carry the page's own campaign so PINK metrics stay complete.
    let sid;
    try { sid = sessionStorage.getItem("pg_sid"); } catch { /* private mode */ }
    api.post("/analytics/event", { event, ref: ref || "pink", path: window.location.pathname, ...(sid ? { sid } : {}) }).catch(() => {});
  }, [ref]);
  const completed = useRef(false);
  const res = useGet("/parcels/resolve", { id: PARCEL_ID });
  const preview = useGet("/reports/preview-real", { id: PARCEL_ID });
  const stats = useGet("/index/stats");

  const next = useCallback(() => setStep((s) => Math.min(s + 1, STEPS.length - 1)), []);
  const prev = useCallback(() => setStep((s) => Math.max(s - 1, 0)), []);

  useEffect(() => {
    if (vertical) return undefined;
    const onKey = (e) => {
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev, vertical]);

  useEffect(() => {
    // F5 (2026-10-04): route entry fires unconditionally — attribution (ref)
    // is metadata, never a precondition for the product event.
    demoEvent("demo_view");
    if (ref) {
      try { sessionStorage.setItem("pg_demo_ref", ref); } catch { /* private mode */ }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!completed.current && (step === STEPS.length - 1 || vertical)) {
      completed.current = true;
      demoEvent("demo_complete");
    }
  }, [step, vertical, demoEvent]);

  const sections = [
    <StepOpening key="s0" />,
    <StepParcel key="s1" res={res} />,
    <StepStatus key="s2" res={res} />,
    <StepEvidence key="s3" res={res} />,
    <StepReport key="s4" preview={preview} />,
    <StepWatch key="s5" />,
    <StepIndex key="s6" stats={stats} />,
    <StepClosing key="s7" onCta={() => { demoEvent("demo_cta_collab"); setCtaOpen(true); }} onMainSite={() => demoEvent("demo_cta_main")} onPilot={() => demoEvent("demo_cta_pilot")} />,
  ];

  return (
    <div data-testid="pink-demo-route-container" className="mx-auto max-w-5xl px-4 pb-28 pt-6 sm:px-6">
      <Helmet>
        <title>PlanGmina dla PINK — demonstracja instytucjonalna</title>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="description" content="Prezentacja PlanGmina dla Polskiej Izby Nieruchomości Komercyjnych: dane planistyczne działek z pełnym pochodzeniem źródeł." />
      </Helmet>

      <header data-testid="pink-demo-stepper-header" className="sticky top-0 z-30 -mx-4 border-b border-slate-200 bg-white/85 px-4 py-3 backdrop-blur-md sm:-mx-6 sm:px-6">
        <div className="flex items-center justify-between gap-3">
          <p className="flex items-center gap-2 font-heading text-xs font-bold uppercase tracking-widest text-slate-500">
            <img src="/logo.png" alt="PlanGmina" className="h-5 w-auto" />
            <span className="text-[#DC143C]">× PINK</span>
          </p>
          <div className="flex items-center gap-2">
            {!vertical && (
              <span data-testid="pink-demo-step-counter" className="text-xs font-semibold text-slate-500">
                Krok {step + 1} z {STEPS.length}
              </span>
            )}
            <Button
              data-testid="pink-demo-view-toggle"
              variant="outline"
              size="sm"
              onClick={() => setVertical((v) => !v)}
            >
              {vertical ? <Presentation size={14} className="mr-1.5" /> : <ScrollText size={14} className="mr-1.5" />}
              {vertical ? "Widok prezentacji" : "Widok pionowy"}
            </Button>
          </div>
        </div>
        {!vertical && (
          <div className="mt-2.5">
            <div className="h-1 w-full rounded-full bg-slate-100">
              <div
                data-testid="pink-demo-progress-bar"
                className="h-1 rounded-full bg-[#DC143C] transition-[width] duration-300"
                style={{ width: `${((step + 1) / STEPS.length) * 100}%` }}
              />
            </div>
            <div className="mt-2 hidden gap-1 md:flex">
              {STEPS.map((s, i) => (
                <button
                  key={s.id}
                  data-testid={`pink-demo-stepper-${s.id}`}
                  onClick={() => setStep(i)}
                  className={`flex-1 rounded-md px-2 py-1.5 text-[11px] font-semibold transition-colors ${
                    i === step ? "bg-slate-900 text-white" : i < step ? "text-slate-700 hover:bg-slate-100" : "text-slate-400 hover:bg-slate-100"
                  }`}
                >
                  {i + 1}. {s.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </header>

      <main className="mt-6 space-y-6">
        {vertical
          ? sections.map((s, i) => <StepShell key={STEPS[i].id} index={i}>{s}</StepShell>)
          : <StepShell index={step}>{sections[step]}</StepShell>}
      </main>

      {!vertical && (
        <nav data-testid="pink-demo-nav-bar" className="fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-white/90 backdrop-blur-md">
          <div className="mx-auto flex max-w-5xl items-center justify-between gap-3 px-4 py-3 sm:px-6">
            <Button data-testid="pink-demo-btn-prev" variant="outline" onClick={prev} disabled={step === 0}>
              <ArrowLeft size={15} className="mr-1.5" /> Wstecz
            </Button>
            <span className="hidden text-xs text-slate-400 sm:block">Strzałki ← → również działają</span>
            <Button
              data-testid="pink-demo-btn-next"
              onClick={next}
              disabled={step === STEPS.length - 1}
              className="bg-[#DC143C] text-white hover:bg-[#B91C1C]"
            >
              Dalej <ArrowRight size={15} className="ml-1.5" />
            </Button>
          </div>
        </nav>
      )}

      <Dialog open={ctaOpen} onOpenChange={setCtaOpen}>
        <DialogContent data-testid="pink-demo-cta-dialog">
          <DialogHeader>
            <DialogTitle>Porozmawiajmy o możliwej współpracy</DialogTitle>
            <DialogDescription>
              Ta prezentacja to punkt wyjścia do rozmowy o pilotażu dla środowiska PINK — w zakresie
              wstępnej analizy gruntów, due diligence nieruchomości komercyjnych i monitoringu portfela działek.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap items-center gap-3 pt-2">
            <Button data-testid="pink-demo-cta-dialog-main" className="bg-[#DC143C] text-white hover:bg-[#B91C1C]" asChild>
              <Link to="/" onClick={() => demoEvent("demo_cta_main")}>Przejdź do PlanGmina.pl</Link>
            </Button>
            <p className="w-full text-xs text-slate-500">{DOCTRINE}</p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
